"use client";

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ClipboardList, Eye, Users, PlusCircle } from "lucide-react";
import { AddAssignmentDialog } from "./add-assignment-dialog";
import { createAssignment } from "@/lib/assignment";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { AuditPlan, AuditPlanAssignment } from "@/lib/audit-plan";

type Props = {
  assignments: AuditPlanAssignment[];
  auditPlan: AuditPlan;
};

export const AssignmentsList = ({ assignments, auditPlan }: Props) => {
  const { toast } = useToast();
  const [isAddNewAssignment, setIsAddNewAssignment] = useState(false);
  const auditPlanId = auditPlan.id;
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createAssignment,
    onSuccess: () => {
      setIsAddNewAssignment(false);
      toast({ title: "Success", description: "New assignment created" });
      queryClient.invalidateQueries({
        queryKey: ["auditPlans", auditPlanId],
      });
    },
  });

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Related Assignments
              </CardTitle>
              <CardDescription className="mt-1">
                All assignments linked to this audit plan
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAddNewAssignment(true)}
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Create Assignment
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {assignments.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Assignment Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignments.map((assignment) => {
                    return (
                      <TableRow key={assignment.id}>
                        <TableCell className="font-medium">{assignment.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {assignment.type.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{assignment.state}</Badge>
                        </TableCell>
                        <TableCell>
                          {assignment.completionPercentage != null &&
                          assignment.completionPercentage > 0 ? (
                            <div className="flex items-center gap-2">
                              <Progress
                                value={assignment.completionPercentage}
                                className="w-16"
                              />
                              <span className="text-sm text-muted-foreground">
                                {assignment.completionPercentage}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to="/assignments/$assignmentId" params={{ assignmentId: assignment.id }}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No Assignments Yet</h3>
              <p className="text-sm text-muted-foreground">
                There are no assignments linked to this audit plan.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <AddAssignmentDialog
        open={isAddNewAssignment}
        onOpenChange={setIsAddNewAssignment}
        mutation={createMutation}
        auditPlan={auditPlan}
      />
    </>
  );
};
